/**
 * Single source for frontend SPA module paths (canonical + legacy aliases).
 * Permission keys and moduleView keys stay internal; only browser URLs live here.
 */

export const PRODUCTS_PATH = '/products';
export const INCOMING_STOCK_PATH = '/incoming-stock';
export const PLANNER_PATH = '/inventory-planner';
export const PRICEMOLE_PATH = '/pricemole';
export const JOBS_3D_PATH = '/3d';
export const REPAIRS_PATH = '/repairs';
export const PICKING_SUPERVISOR_PATH = '/picking/supervisor';
export const PACKING_SUPERVISOR_PATH = '/picking/supervisor/packing';
export const PICKUP_KIOSK_PATH = '/pickup-kiosk';
/** Packing bench. Not in the header nav until Starshipit canary; type /ship. */
export const SHIP_PATH = '/ship';
/** @deprecated Use PICKUP_KIOSK_PATH — kept for legacy redirect matching */
export const LEGACY_PICK_FROM_STORE_PATH = '/pick-from-store';
export const LOCAL_DELIVERIES_PATH = '/local';
/** @deprecated Use LOCAL_DELIVERIES_PATH — kept for legacy redirect matching */
export const LEGACY_LOCAL_DELIVERIES_PATH = '/local-deliveries';

/** @deprecated Use PRODUCTS_PATH — kept for legacy redirect matching */
export const LEGACY_WAREHOUSE_PATH = '/warehouse';
/** @deprecated Use INCOMING_STOCK_PATH */
export const LEGACY_POS_PATH = '/pos';
/** @deprecated Use PLANNER_PATH */
export const LEGACY_PLANNER_PATH = '/po-planning';

export function normalizePathname(pathname = '') {
  const path = String(pathname || '').split('?')[0].replace(/\/+$/, '');
  return path || '/';
}

export function isProductsPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === PRODUCTS_PATH || path === LEGACY_WAREHOUSE_PATH || path === '/';
}

/** @deprecated Use isProductsPath */
export const isWarehousePath = isProductsPath;

export function productsHomePath() {
  return PRODUCTS_PATH;
}

export function isJobs3dPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === JOBS_3D_PATH;
}

export function isRepairsPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === REPAIRS_PATH;
}

/**
 * Pickup kiosk — the click-and-collect collection board.
 * The internal `moduleView` key and the `/api/pick-from-store` mount keep their
 * original names on purpose (AGENTS.md: only browser paths track user-facing
 * naming), so the old path is accepted and redirected rather than broken.
 */
/**
 * The kiosk's two panes. `/pickup-kiosk` is the Pickups pane; `/pickup-kiosk/lockers`
 * opens the Lockers pane directly (2026-09-14) so it can be bookmarked on the
 * wall tablet. Mirrors PANES in lockerDisplay.js (kept literal here so the
 * route map stays dependency-free).
 */
export const PICKUP_KIOSK_PANES = ['pickups', 'lockers'];
export const DEFAULT_PICKUP_KIOSK_PANE = 'pickups';

function pickupKioskSegments(pathname = '') {
  const path = normalizePathname(pathname);
  for (const base of [PICKUP_KIOSK_PATH, LEGACY_PICK_FROM_STORE_PATH]) {
    if (path === base) return [];
    if (path.startsWith(`${base}/`)) return path.slice(base.length + 1).split('/').filter(Boolean);
  }
  return null;
}

export function isPickupKioskPath(pathname = '') {
  const segments = pickupKioskSegments(pathname);
  if (segments === null) return false;
  if (segments.length === 0) return true;
  return segments.length === 1 && PICKUP_KIOSK_PANES.includes(segments[0].toLowerCase());
}

/** Which kiosk pane a path opens: 'pickups' (default) or 'lockers'. */
export function parsePickupKioskPane(pathname = '') {
  const segments = pickupKioskSegments(pathname);
  if (!segments || segments.length !== 1) return DEFAULT_PICKUP_KIOSK_PANE;
  const pane = segments[0].toLowerCase();
  return PICKUP_KIOSK_PANES.includes(pane) ? pane : DEFAULT_PICKUP_KIOSK_PANE;
}

/** Canonical URL for a kiosk pane: the default pane is the bare module path. */
export function pickupKioskPanePath(pane) {
  const key = String(pane || '').trim().toLowerCase();
  if (!PICKUP_KIOSK_PANES.includes(key) || key === DEFAULT_PICKUP_KIOSK_PANE) return PICKUP_KIOSK_PATH;
  return `${PICKUP_KIOSK_PATH}/${key}`;
}

/** Ship packing bench — one queue for every store. `/ship/settings` is the settings module. */
export const SHIP_SETTINGS_PATH = `${SHIP_PATH}/settings`;

/**
 * Ship deep links (2026-09-14). Every bench screen has a URL that reopens it:
 *   /ship                                   board, Packing Queue tab
 *   /ship/<tab>                             board, another tab (held | printed | shipped | archived | statistics | packaging)
 *   /ship/held                              Held tab (carrier down mode, 2026-10-08); never carries an order
 *   /ship/statistics?from&to&storeKey…      Statistics tab; its filters live in the query (`route.search`)
 *   /ship/packaging?view=reorder            Packaging tab (2026-10-08); its sub-tab lives in the query
 *   /ship/<tab>/<orderKey>                  that order open (validation on packing, history elsewhere)
 *   /ship/packing/<orderKey>/packaging      the packaging step of a packing order
 *   /ship/settings[/<section>]              settings module, one section
 * <orderKey> is orderSelectionKey(order) ("store:orderId[:dispatchId]"), URL-encoded.
 */
export const SHIP_TABS = ['packing', 'held', 'printed', 'shipped', 'archived', 'statistics', 'packaging'];
/** Tabs whose query string is part of the route (`route.search`); the others drop it. */
const SHIP_TABS_WITH_SEARCH = ['statistics', 'packaging'];
/** Tabs that are a page of their own, never an order list: no order key in the URL. */
const SHIP_TABS_WITHOUT_ORDERS = ['held', ...SHIP_TABS_WITH_SEARCH];
export const DEFAULT_SHIP_TAB = 'packing';
export const SHIP_PACK_STEPS = ['validate', 'packaging'];
export const SHIP_SETTINGS_SECTIONS = [
  'pickups', 'printing', 'couriers', 'senders', 'cutover',
  'rules', 'packaging', 'emails', 'benches', 'returns', 'escalation'
];
export const DEFAULT_SHIP_SETTINGS_SECTION = 'couriers';

/**
 * Sections that split into sub-tabs, mirrored as /ship/settings/<section>/<sub>.
 * The first entry is the default and is left out of the URL.
 */
export const SHIP_SETTINGS_SUBSECTIONS = {
  couriers: ['accounts', 'booking', 'charges', 'rates'],
  emails: ['email', 'sms', 'logs']
};

/**
 * Sections that used to be their own nav entry and are now a sub-tab. Old deep
 * links keep working: /ship/settings/charges lands on Carriers → Charges.
 */
export const LEGACY_SHIP_SETTINGS_SECTIONS = {
  charges: { section: 'couriers', subsection: 'charges' },
  sms: { section: 'emails', subsection: 'sms' }
};

/** The sub-tab list for a section, or null when it has none. */
export function shipSettingsSubsections(section) {
  return SHIP_SETTINGS_SUBSECTIONS[String(section || '').toLowerCase()] || null;
}

/**
 * Resolve a (possibly legacy, possibly unknown) section/sub-tab pair to the
 * section and sub-tab actually rendered.
 */
export function resolveShipSettingsSection(section, subsection) {
  const wanted = String(section || '').toLowerCase();
  const legacy = LEGACY_SHIP_SETTINGS_SECTIONS[wanted];
  const resolvedSection = legacy
    ? legacy.section
    : (SHIP_SETTINGS_SECTIONS.includes(wanted) ? wanted : DEFAULT_SHIP_SETTINGS_SECTION);
  const subs = shipSettingsSubsections(resolvedSection);
  if (!subs) return { section: resolvedSection, subsection: null };
  const wantedSub = String((legacy && legacy.subsection) || subsection || '').toLowerCase();
  return {
    section: resolvedSection,
    subsection: subs.includes(wantedSub) ? wantedSub : subs[0]
  };
}

export function isShipPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === SHIP_PATH || path.startsWith(`${SHIP_PATH}/`);
}

function decodeSegment(segment) {
  try { return decodeURIComponent(segment); } catch (_e) { return segment; }
}

/**
 * Parse a Ship URL into `{ panel, tab, orderKey, step, section, subsection, search }`.
 * Unknown tabs or sections fall back to the defaults rather than throwing, so
 * a stale link still lands somewhere sensible. `search` is the query string
 * without its `?` ('' when none); only the Statistics tab keeps it (its filters
 * live there), every other screen gets ''. The query comes from `pathname`
 * itself (`/ship/statistics?from=…`) or from the optional `search` argument
 * (`window.location.search`).
 */
export function parseShipRoute(pathname = '', search = '') {
  const raw = String(pathname || '');
  const queryIndex = raw.indexOf('?');
  const query = String(search || (queryIndex >= 0 ? raw.slice(queryIndex) : '')).replace(/^\?/, '');
  const path = normalizePathname(raw);
  const route = { panel: 'board', tab: DEFAULT_SHIP_TAB, orderKey: null, step: 'validate', section: null, subsection: null, search: '' };
  if (!isShipPath(path)) return route;
  const segments = path === SHIP_PATH ? [] : path.slice(SHIP_PATH.length + 1).split('/').filter(Boolean);
  if (!segments.length) return route;
  const head = segments[0].toLowerCase();
  if (head === 'settings') {
    const resolved = resolveShipSettingsSection(segments[1], segments[2]);
    return { ...route, panel: 'settings', ...resolved };
  }
  if (!SHIP_TABS.includes(head)) return route;
  route.tab = head;
  if (SHIP_TABS_WITH_SEARCH.includes(head)) {
    route.search = query;
    return route;
  }
  if (SHIP_TABS_WITHOUT_ORDERS.includes(head)) return route;
  if (segments[1]) route.orderKey = decodeSegment(segments[1]);
  if (route.orderKey && route.tab === 'packing' && (segments[2] || '').toLowerCase() === 'packaging') {
    route.step = 'packaging';
  }
  return route;
}

/** Build the canonical URL for a Ship route (inverse of parseShipRoute). */
export function shipRoutePath(route = {}) {
  if (route?.panel === 'settings') {
    const { section, subsection } = resolveShipSettingsSection(route.section, route.subsection);
    const subs = shipSettingsSubsections(section);
    if (subs && subsection !== subs[0]) return `${SHIP_SETTINGS_PATH}/${section}/${subsection}`;
    if (section === DEFAULT_SHIP_SETTINGS_SECTION) return SHIP_SETTINGS_PATH;
    return `${SHIP_SETTINGS_PATH}/${section}`;
  }
  const tab = SHIP_TABS.includes(route?.tab) ? route.tab : DEFAULT_SHIP_TAB;
  if (SHIP_TABS_WITH_SEARCH.includes(tab)) {
    const query = String(route?.search || '').replace(/^\?/, '');
    return `${SHIP_PATH}/${tab}${query ? `?${query}` : ''}`;
  }
  if (SHIP_TABS_WITHOUT_ORDERS.includes(tab)) return `${SHIP_PATH}/${tab}`;
  const orderKey = route?.orderKey ? String(route.orderKey) : '';
  if (!orderKey) return tab === DEFAULT_SHIP_TAB ? SHIP_PATH : `${SHIP_PATH}/${tab}`;
  const base = `${SHIP_PATH}/${tab}/${encodeURIComponent(orderKey)}`;
  return tab === 'packing' && route?.step === 'packaging' ? `${base}/packaging` : base;
}

/** Which Ship panel a path opens: 'board' or 'settings'. */
export function parseShipPanelFromPath(pathname = '') {
  return parseShipRoute(pathname).panel;
}

/** @deprecated Use isPickupKioskPath */
export const isPickFromStorePath = isPickupKioskPath;

/** Local Deliveries — the delivery run board (phone-first). */
export function isLocalDeliveriesPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === LOCAL_DELIVERIES_PATH || path === LEGACY_LOCAL_DELIVERIES_PATH;
}

export function isIncomingStockPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === INCOMING_STOCK_PATH || path === LEGACY_POS_PATH;
}

export function incomingStockPath(tab, { po } = {}) {
  // Must match App.js's no-?tab= fallback, or a tab's own URL round-trips to
  // the wrong tab on reload/back (2026-08-29: default moved to 'arrived' -
  // the put-away starting point on the warehouse tablet).
  const defaultTab = 'arrived';
  const params = new URLSearchParams();
  if (tab && tab !== defaultTab) params.set('tab', tab);
  // `?po=<reference>` opens the PO manager filtered to one PO; the CEO
  // watchlist links each arriving PO this way (2026-10-23).
  const poRef = po == null ? '' : String(po).trim();
  if (poRef) params.set('po', poRef);
  const query = params.toString();
  return query ? `${INCOMING_STOCK_PATH}?${query}` : INCOMING_STOCK_PATH;
}

export function isPlannerPath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === PLANNER_PATH
    || path.startsWith(`${PLANNER_PATH}/`)
    || path === LEGACY_PLANNER_PATH
    || path.startsWith(`${LEGACY_PLANNER_PATH}/`);
}

export function parsePlannerChannelFromPath(pathname = '') {
  const path = normalizePathname(pathname);
  if (!isPlannerPath(path)) return 'all';

  const segment = path.split('/').filter(Boolean).pop()?.toLowerCase() || '';
  if (segment === 'all') return 'all';
  if (segment === 'phaserfpv') return 'phaserfpv';
  if (segment === 'centralcoastcoffee' || segment === 'ccc') return 'centralcoastcoffee';
  if (segment === 'inventory-planner' || segment === 'po-planning') return 'all';
  return 'all';
}

export function plannerChannelPath(channelKey) {
  const key = String(channelKey || 'all').trim().toLowerCase();
  if (key === 'all') return `${PLANNER_PATH}/all`;
  if (key === 'phaserfpv' || key === 'centralcoastcoffee') return `${PLANNER_PATH}/${key}`;
  return `${PLANNER_PATH}/all`;
}

function canonicalPlannerPathname(pathname = '') {
  return plannerChannelPath(parsePlannerChannelFromPath(pathname));
}

export function isPricemolePath(pathname = '') {
  const path = normalizePathname(pathname);
  return path === PRICEMOLE_PATH || path.startsWith(`${PRICEMOLE_PATH}/`);
}

/** PriceMole is per-store only (no "all"); CCC is the default view. */
export function parsePricemoleChannelFromPath(pathname = '') {
  const path = normalizePathname(pathname);
  if (!isPricemolePath(path)) return 'centralcoastcoffee';
  const segment = path.split('/').filter(Boolean).pop()?.toLowerCase() || '';
  if (segment === 'phaserfpv') return 'phaserfpv';
  if (segment === 'centralcoastcoffee' || segment === 'ccc') return 'centralcoastcoffee';
  return 'centralcoastcoffee';
}

export function pricemoleChannelPath(channelKey) {
  const key = String(channelKey || '').trim().toLowerCase();
  if (key === 'phaserfpv' || key === 'centralcoastcoffee') return `${PRICEMOLE_PATH}/${key}`;
  if (key === 'ccc') return `${PRICEMOLE_PATH}/centralcoastcoffee`;
  return `${PRICEMOLE_PATH}/centralcoastcoffee`;
}

/**
 * PriceMole is on this list because the Shopify admin PriceMole block deep-links
 * back here as `/pricemole/{channel}?sku=…` — stripping the param would land
 * Rhys on the module with no idea which product he clicked.
 */
export function shouldAllowSkuQueryParam(pathname = '') {
  return isProductsPath(pathname) || isPlannerPath(pathname) || isPricemolePath(pathname);
}

export function stripWarehouseSkuParams(input) {
  const params = input instanceof URLSearchParams
    ? new URLSearchParams(input.toString())
    : new URLSearchParams(String(input || '').replace(/^\?/, ''));
  while (params.has('sku')) {
    params.delete('sku');
  }
  return params;
}

export function buildAppPath(pathname, searchParams = null) {
  const path = normalizePathname(pathname) || '/';
  if (!searchParams) return path;
  const params = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams).replace(/^\?/, ''));
  if ([...params.keys()].length === 0) return path;
  return `${path}?${params.toString()}`;
}

/**
 * Map legacy paths to canonical paths. Preserves query string.
 * @returns {{ pathname: string, search: string, changed: boolean }}
 */
export function resolveCanonicalUrl(pathname = '', search = '') {
  const path = normalizePathname(pathname);
  const searchStr = String(search || '');
  if ((path === '/map' || path === '/warehouse-map-mockup') && new URLSearchParams(searchStr).has('restore')) {
    return { pathname: '/backup-restore', search: searchStr, changed: true };
  }

  if (path === '/' || path === LEGACY_WAREHOUSE_PATH) {
    return { pathname: PRODUCTS_PATH, search: searchStr, changed: path !== PRODUCTS_PATH };
  }
  if (path === LEGACY_POS_PATH) {
    return { pathname: INCOMING_STOCK_PATH, search: searchStr, changed: true };
  }
  if (path === LEGACY_PLANNER_PATH || path.startsWith(`${LEGACY_PLANNER_PATH}/`)) {
    const canonical = canonicalPlannerPathname(path);
    return { pathname: canonical, search: searchStr, changed: canonical !== path };
  }
  if (path === PLANNER_PATH) {
    const canonical = `${PLANNER_PATH}/all`;
    return { pathname: canonical, search: searchStr, changed: true };
  }
  if (path === PRICEMOLE_PATH) {
    const canonical = pricemoleChannelPath('centralcoastcoffee');
    return { pathname: canonical, search: searchStr, changed: true };
  }
  if (path === '/warehouse-map-mockup') {
    return { pathname: '/map', search: searchStr, changed: true };
  }
  if (isPickupKioskPath(path)) {
    // `/pick-from-store[/lockers]` → `/pickup-kiosk[/lockers]`; the default
    // pane's explicit `/pickups` segment collapses to the bare module path.
    const canonical = pickupKioskPanePath(parsePickupKioskPane(path));
    return { pathname: canonical, search: searchStr, changed: canonical !== path };
  }
  if (path === LEGACY_LOCAL_DELIVERIES_PATH) {
    return { pathname: LOCAL_DELIVERIES_PATH, search: searchStr, changed: true };
  }

  return { pathname: path, search: searchStr, changed: false };
}

/** Apply canonical redirect in-place when needed. Returns final pathname. */
export function ensureCanonicalUrl(pathname = window.location.pathname, search = window.location.search) {
  const resolved = resolveCanonicalUrl(pathname, search);
  if (!resolved.changed) return normalizePathname(pathname);

  const params = resolved.search
    ? new URLSearchParams(resolved.search.replace(/^\?/, ''))
    : null;
  const url = buildAppPath(resolved.pathname, params);
  window.history.replaceState({}, '', url);
  return resolved.pathname;
}

/** Remove warehouse ?sku= params when the current route is not allowed to keep them. */
export function normalizeModuleUrl(pathname = window.location.pathname, search = window.location.search) {
  if (shouldAllowSkuQueryParam(pathname)) return null;
  const params = new URLSearchParams(search);
  if (!params.has('sku')) return null;
  const cleaned = stripWarehouseSkuParams(params);
  return buildAppPath(pathname, cleaned);
}

/**
 * Canonical URL each header nav item links to (keyed by the nav key used in
 * moduleConfig.js NAV_KEYS / HEADER_TOOL_KEYS, plus 'tags'). The header renders
 * real <a href> links so right-click "Open link in new tab", middle-click and
 * Ctrl/Cmd-click work; a plain left click still navigates in-app. Every path
 * here must be one App.js handleRoute opens on a fresh load. Keep it in step
 * with the navigateTo* helpers in App.js. Returns null for an unknown key.
 */
export function navHrefForModule(key, { pricemoleChannel } = {}) {
  switch (key) {
    case 'ceo': return CEO_PATH;
    case 'warehouse': return PRODUCTS_PATH;
    case 'picking-supervisor': return PICKING_SUPERVISOR_PATH;
    case 'picking': return '/picking';
    case 'po': return incomingStockPath();
    case 'preorders': return '/preorders';
    case 'ship': return SHIP_PATH;
    case 'map': return '/map';
    case 'pick-from-store': return PICKUP_KIOSK_PATH;
    case 'jobs': return JOBS_3D_PATH;
    case 'ra': return REPAIRS_PATH;
    case 'po-planning': return plannerChannelPath('all');
    case 'new-products': return '/new-products';
    case 'pricemole': return pricemoleChannelPath(pricemoleChannel || 'centralcoastcoffee');
    case 'coffee-stocktake': return '/coffee-stocktake';
    case 'local-deliveries': return LOCAL_DELIVERIES_PATH;
    case 'backup-restore': return '/backup-restore';
    case 'expiry': return EXPIRY_PATH;
    case 'tags': return '/tags';
    default: return null;
  }
}

/** Food expiry dashboard (warehouse module). */
export const EXPIRY_PATH = '/expiry';

/** CEO master watchlist (admin-only `ceo` grant, 2026-10-23). */
export const CEO_PATH = '/ceo';

export function isCeoPath(pathname = '') {
  return normalizePathname(pathname) === CEO_PATH;
}

export function isExpiryPath(pathname = '') {
  return normalizePathname(pathname) === EXPIRY_PATH;
}

/** Warehouse tray stocktake (opened from the Picking header). */
export const STOCKTAKE_PATH = '/stocktake';

export function isStocktakePath(pathname = '') {
  return normalizePathname(pathname) === STOCKTAKE_PATH;
}

/** `/stocktake?location=W-01-D-01` deep link; empty location is the home screen. */
export function stocktakeTrayPath(location) {
  const code = String(location || '').trim();
  return code ? `${STOCKTAKE_PATH}?location=${encodeURIComponent(code)}` : STOCKTAKE_PATH;
}

export function parseStocktakeLocation(search = '') {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const code = String(params.get('location') || '').trim().toUpperCase();
  return code || null;
}
